// ============================================
// OFFLINE DB — IndexedDB Wrapper
// Zeno ERP v18 — 16 Jun 2026
// ============================================

const DB_NAME = "zeno_offline";
const DB_VERSION = 1;

const STORES = {
  JOB_ACTIONS: "job_actions",   // accept, start, complete events
  JOB_REPORTS: "job_reports",   // remarks, defective items
  SYNC_QUEUE: "sync_queue",     // pending Firestore writes
};

// ── Open DB ──────────────────────────────────
let _db = null;

export const openDB = () => {
  return new Promise((resolve, reject) => {
    if (_db) return resolve(_db);

    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      if (!db.objectStoreNames.contains(STORES.JOB_ACTIONS)) {
        const s = db.createObjectStore(STORES.JOB_ACTIONS, { keyPath: "id", autoIncrement: true });
        s.createIndex("service_id", "service_id", { unique: false });
        s.createIndex("synced", "synced", { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.JOB_REPORTS)) {
        const s = db.createObjectStore(STORES.JOB_REPORTS, { keyPath: "service_id" });
        s.createIndex("synced", "synced", { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const s = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: "id", autoIncrement: true });
        s.createIndex("synced", "synced", { unique: false });
      }
    };

    req.onsuccess = (e) => {
      _db = e.target.result;
      resolve(_db);
    };

    req.onerror = () => reject(req.error);
  });
};

// ── Generic helpers ───────────────────────────
const withStore = async (storeName, mode, fn) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const getAllFromStore = async (storeName, indexName, value) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const req = indexName
      ? store.index(indexName).getAll(value)
      : store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

// ── JOB ACTIONS ──────────────────────────────
// Save action offline (accept / start / complete)
export const saveJobAction = async (serviceId, action, payload) => {
  return withStore(STORES.JOB_ACTIONS, "readwrite", (store) =>
    store.add({
      service_id: serviceId,
      action,                        // "accept" | "start" | "complete"
      payload,                       // { uid, name, coords, remarks, etc }
      timestamp: new Date().toISOString(),
      synced: 0,
      retry_count: 0,
    })
  );
};

// Get all unsynced actions
export const getUnsyncedActions = async () => {
  return getAllFromStore(STORES.JOB_ACTIONS, "synced", 0);
};

// Mark action synced
export const markActionSynced = async (id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JOB_ACTIONS, "readwrite");
    const store = tx.objectStore(STORES.JOB_ACTIONS);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.synced = 1;
        record.synced_at = new Date().toISOString();
        store.put(record);
      }
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

// Increment retry count on failure
export const incrementRetry = async (id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JOB_ACTIONS, "readwrite");
    const store = tx.objectStore(STORES.JOB_ACTIONS);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.retry_count = (record.retry_count || 0) + 1;
        store.put(record);
      }
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

// ── JOB REPORTS ──────────────────────────────
// Save/update report offline
export const saveJobReport = async (serviceId, reportData) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JOB_REPORTS, "readwrite");
    const store = tx.objectStore(STORES.JOB_REPORTS);
    const getReq = store.get(serviceId);
    getReq.onsuccess = () => {
      const existing = getReq.result || { service_id: serviceId, synced: 0 };
      const updated = {
        ...existing,
        ...reportData,
        updated_at: new Date().toISOString(),
        synced: 0,
      };
      const putReq = store.put(updated);
      putReq.onsuccess = () => resolve(putReq.result);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

// Get report for a job
export const getJobReport = async (serviceId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JOB_REPORTS, "readonly");
    const store = tx.objectStore(STORES.JOB_REPORTS);
    const req = store.get(serviceId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
};

// Get all unsynced reports
export const getUnsyncedReports = async () => {
  return getAllFromStore(STORES.JOB_REPORTS, "synced", 0);
};

// Mark report synced
export const markReportSynced = async (serviceId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.JOB_REPORTS, "readwrite");
    const store = tx.objectStore(STORES.JOB_REPORTS);
    const getReq = store.get(serviceId);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (record) {
        record.synced = 1;
        record.synced_at = new Date().toISOString();
        store.put(record);
      }
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

export { STORES };